class TagalysQuickSearch extends SearchForm {
  constructor () {
    super()

    this.tagalysQuickSearchTimeout = 6000
    this.cachedResults = {}
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.predictiveSearchContainer = this.querySelector('#predictive-search-container')
    this.isOpen = false
    this.searchTerm = ''
    this.searchSuggestions = null
    this._initialized = false
    this._pendingQuery = ''
    this._tagalysRetryTimer = null
    this._tagalysSearchDisabled = false
    this._suggestionsSettled = true
    this._suggestionsTimeoutId = null
    this.abortController = new AbortController()
    this.cachedProductCards = {}

    this.queryCount = 3
    this.collectionCount = 3
    this.productCount = 4

    this.handleSuggestionsSuccess = this.handleSuggestionsSuccess.bind(this)
    this.handleSuggestionsFailure = this.handleSuggestionsFailure.bind(this)
    this.onSearchModalOpened = this.onSearchModalOpened.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onFocusOut = this.onFocusOut.bind(this)
    this.onKeyup = this.onKeyup.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onTagalysReady = this.onTagalysReady.bind(this)

    this.setupEventListeners()
    this.initTagalys()
  }

  connectedCallback () {
    this.details = this.closest('details')

    if (!this.details || this.boundOnDetailsToggle) return

    this.boundOnDetailsToggle = this.onDetailsToggle.bind(this)
    this.details.addEventListener('toggle', this.boundOnDetailsToggle)
  }

  disconnectedCallback () {
    this.details?.removeEventListener('toggle', this.boundOnDetailsToggle)
    this.boundOnDetailsToggle = null
    this.details = null
    this.input?.form?.removeEventListener('submit', this.onFormSubmit)
    this.input?.removeEventListener('focus', this.onFocus)
    this.removeEventListener('focusout', this.onFocusOut)
    this.removeEventListener('keyup', this.onKeyup)
    this.removeEventListener('keydown', this.onKeydown)
    this.input?.removeEventListener('searchModalOpened', this.onSearchModalOpened)
    document.removeEventListener('tagalysReady', this.onTagalysReady)
    clearTimeout(this.focusOutTimer)
    clearTimeout(this.liveRegionTimer)
    clearTimeout(this._tagalysRetryTimer)
    clearTimeout(this._suggestionsTimeoutId)
    this.abortController?.abort()
  }

  setupEventListeners () {
    this.input?.form?.addEventListener('submit', this.onFormSubmit)
    this.input?.addEventListener('focus', this.onFocus)
    this.addEventListener('focusout', this.onFocusOut)
    this.addEventListener('keyup', this.onKeyup)
    this.addEventListener('keydown', this.onKeydown)
    this.input?.addEventListener('searchModalOpened', this.onSearchModalOpened)
  }

  initTagalys () {
    if (window.tagalysReady && window.Tagalys) {
      this.onTagalysReady()
      return
    }

    document.addEventListener('tagalysReady', this.onTagalysReady, { once: true })

    if (typeof window.onTagalysReady === 'function') {
      window.onTagalysReady(this.onTagalysReady)
    }

    this.scheduleTagalysRetry()
  }

  scheduleTagalysRetry () {
    if (this._initialized || this._tagalysRetryTimer) return

    const retry = (attempt = 0) => {
      if (this._initialized) return
      if (window.Tagalys) this.onTagalysReady()
      if (this._initialized || attempt >= 20) return
      this._tagalysRetryTimer = setTimeout(() => retry(attempt + 1), 150)
    }

    retry()
  }

  onTagalysReady () {
    this.initialiseSuggestions()
    this.fetchZeroStateRecommendations()
  }

  fetchZeroStateRecommendations () {
    const recommendations = this.querySelector('tagalys-recommendations')
    if (!recommendations) return

    if (typeof recommendations.fetchTagalysRecommendations === 'function') {
      recommendations.fetchTagalysRecommendations()
    } else if (window.customElements?.whenDefined) {
      customElements.whenDefined('tagalys-recommendations').then(() => {
        recommendations.fetchTagalysRecommendations?.()
      })
    }
  }

  initialiseSuggestions () {
    const SearchSuggestions =
      window.Tagalys?.APIConnector?.SearchSuggestions ||
      window.Tagalys?.UIWidgets?.SearchSuggestions

    if (this._initialized || !SearchSuggestions) return

    this._initialized = true
    clearTimeout(this._tagalysRetryTimer)

    this.searchSuggestions = SearchSuggestions.new({
      params: {
        query: this.getQuery() || this._pendingQuery,
        sections: this.getSuggestionSections(),
      },
      onSuccess: this.handleSuggestionsSuccess,
      onFailure: this.handleSuggestionsFailure,
    })

    this.armSuggestionsTimeout()

    if (this._pendingQuery) {
      this.requestSuggestions(this._pendingQuery)
    }
  }

  armSuggestionsTimeout () {
    this._suggestionsSettled = false
    clearTimeout(this._suggestionsTimeoutId)

    this._suggestionsTimeoutId = setTimeout(() => {
      if (this._suggestionsSettled) return
      this._suggestionsSettled = true
      console.error('[TagalysQuickSearch] Suggestions request timed out')
      this.fetchShopifySuggestions(this.getQuery() || this._pendingQuery)
    }, this.tagalysQuickSearchTimeout)
  }

  getSuggestionSections () {
    const sections = {}

    if (this.queryCount > 0) {
      sections.queries = {
        count: this.queryCount,
      }
    }

    if (this.collectionCount > 0) {
      sections.collections = {
        count: this.collectionCount,
      }
    }

    if (this.productCount > 0) {
      sections.products = {
        include: ['total_count', 'items'],
        count: this.productCount,
      }
    }

    return sections
  }

  getQuery () {
    return this.input?.value.trim() || ''
  }

  onChange () {
    super.onChange()
    const newSearchTerm = this.getQuery()

    if (!this.searchTerm) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove()
    }

    this.searchTerm = newSearchTerm

    if (this.searchTerm.length) {
      this.predictiveSearchContainer?.classList.remove('empty')
      this.getSearchResults(this.searchTerm)
      return
    }

    this.predictiveSearchContainer?.classList.add('empty')
    this.removeAttribute('results')
    this.predictiveSearchResults?.removeAttribute('loading')
  }

  onFormSubmit (event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault()
    }
  }

  onFormReset (event) {
    super.onFormReset(event)
    if (super.shouldResetForm()) {
      this.searchTerm = ''
      this._pendingQuery = ''
      this.abortController.abort()
      this.abortController = new AbortController()
      this.searchSuggestions?.helpers?.updateQuery('')
      this.closeResults(true)

      if (this.details?.hasAttribute('open')) {
        this.showEmptyState()
        return
      }

      this.isOpen = false
    }
  }

  onSearchModalOpened () {
    this.syncWithModal()
  }

  onDetailsToggle () {
    if (this.details.hasAttribute('open')) {
      this.syncWithModal()
      return
    }

    this.close(false)
  }

  syncWithModal () {
    const term = this.getQuery()

    if (!term.length) {
      this.showEmptyState()
      return
    }

    this.open()

    if (this.searchTerm !== term || this.getAttribute('results') !== 'true') {
      this.getSearchResults(term)
      return
    }

    this.predictiveSearchContainer?.classList.remove('empty')
  }

  showEmptyState () {
    this.predictiveSearchContainer?.classList.add('empty')
    this.open()
  }

  onFocus () {
    const currentSearchTerm = this.getQuery()

    if (!currentSearchTerm.length) {
      this.open()
      this.predictiveSearchContainer?.classList.add('empty')
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange()
    } else if (this.getAttribute('results') === 'true' && !this.isOpen) {
      this.open()
    } else if (currentSearchTerm.length) {
      this.getSearchResults(this.searchTerm)
    }
  }

  onFocusOut () {
    clearTimeout(this.focusOutTimer)
    this.focusOutTimer = setTimeout(() => {
      if (this.details?.hasAttribute('open')) return
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  onKeyup (event) {
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  onKeydown (event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item'),
    ).filter((element) => element.offsetParent !== null)

    if (moveUp && !selectedElement) return

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement)

    if (this.statusElement) this.statusElement.textContent = ''

    let activeElementIndex = 0

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1
    }

    if (activeElementIndex === selectedElementIndex) return

    const activeElement = allVisibleElements[activeElementIndex]
    if (!activeElement) return

    activeElement.setAttribute('aria-selected', true)
    if (selectedElement) selectedElement.setAttribute('aria-selected', false)

    this.input?.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption () {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    )

    if (selectedOption) selectedOption.click()
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replaceAll(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    this.abortController.abort()
    this.abortController = new AbortController()

    if (!this.searchSuggestions) {
      this.initTagalys()
    }

    this.requestSuggestions(searchTerm)
  }

  requestSuggestions (searchTerm) {
    this._pendingQuery = searchTerm

    if (this._tagalysSearchDisabled) {
      this.fetchShopifySuggestions(searchTerm)
      return
    }

    if (!this.searchSuggestions?.helpers?.updateQuery) {
      this.open()
      return
    }

    this.searchSuggestions.helpers.updateQuery(searchTerm)

    if (typeof this.searchSuggestions.helpers.call === 'function') {
      this.armSuggestionsTimeout()
      this.searchSuggestions.helpers.call()
    }
  }

  handleSuggestionsSuccess (response) {
    this._suggestionsSettled = true
    clearTimeout(this._suggestionsTimeoutId)

    const currentQuery = this.getQuery()
    if (!currentQuery.length) return

    this.buildResultsMarkup(response, currentQuery)
      .then((resultsMarkup) => {
        if (this.getQuery() !== currentQuery) return

        const queryKey = currentQuery.replaceAll(' ', '-').toLowerCase()
        this.cachedResults[queryKey] = resultsMarkup
        this._pendingQuery = ''
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        this.close()
        console.error('[TagalysQuickSearch] Failed to render suggestions', error)
      })
  }

  handleSuggestionsFailure (response) {
    if (this._suggestionsSettled) return
    this._suggestionsSettled = true
    clearTimeout(this._suggestionsTimeoutId)

    const featureDisabled = response?.error?.code === 'feature_not_enabled'
    if (featureDisabled) this._tagalysSearchDisabled = true

    console.error('[TagalysQuickSearch] Suggestions request failed:', response)

    this.fetchShopifySuggestions(this.getQuery() || this._pendingQuery)
  }

  fetchShopifySuggestions (searchTerm) {
    const searchUrl = window.routes?.predictiveSearchUrl || window.routes?.predictive_search_url

    if (!searchUrl || !searchTerm) {
      this.predictiveSearchResults?.removeAttribute('loading')
      this.removeAttribute('loading')
      return
    }

    const queryKey = `shopify-${searchTerm.replaceAll(' ', '-').toLowerCase()}`

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    fetch(
      `${searchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,query,collection&resources[limit]=4&resources[limit_scope]=each`,
      { signal: this.abortController.signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status)
        return response.text()
      })
      .then((text) => {
        if (this.getQuery() !== searchTerm) return

        const parsedDoc = new DOMParser().parseFromString(text, 'text/html')
        const resultsMarkup = parsedDoc.querySelector('#shopify-section-predictive-search')?.innerHTML
        if (!resultsMarkup) throw new Error('Missing Shopify predictive search markup')

        this.cachedResults[queryKey] = resultsMarkup
        this._pendingQuery = ''
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return
        this.predictiveSearchResults?.removeAttribute('loading')
        this.removeAttribute('loading')
        console.error('[TagalysQuickSearch] Shopify fallback failed', error)
      })
  }

  async buildResultsMarkup (response, searchTerm) {
    const strings = window.strings?.searchTemplate || {}
    const queryItems = this.getQueryItems(response)
    const collectionItems = this.getCollectionItems(response)
    const suggestionItems = this.getSuggestionItems(response)
    const productItems = this.getProductItems(response)

    const hasSuggestions = suggestionItems.length > 0
    const hasProducts = productItems.length > 0
    const wrapperClasses = [
      'predictive-search__results-groups-wrapper',
      !hasProducts ? 'predictive-search__results-groups-wrapper--no-products' : '',
      !hasSuggestions ? 'predictive-search__results-groups-wrapper--no-suggestions' : '',
    ]
      .filter(Boolean)
      .join(' ')

    const productCards = hasProducts ? await this.createProductCards(productItems) : ''
    const searchHref = this.buildSearchHref(searchTerm, response)

    const queryList = queryItems
      .map((item, index) => this.buildTextResultItem(item, `query-${index + 1}`))
      .join('')
    const collectionList = collectionItems
      .map((item, index) => this.buildTextResultItem(item, `collection-${index + 1}`))
      .join('')
    const suggestionList = `${queryList}${collectionList}`

    return `
      <div id="predictive-search-results" class="page-width" role="listbox">
        ${
          hasSuggestions || hasProducts
            ? `<div id="predictive-search-results-groups-wrapper" class="${wrapperClasses}">`
            : ''
        }
        ${
          hasSuggestions
            ? `<div class="predictive-search__result-group">
                <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">${this.escapeHtml(strings.suggestions || '')}</h2>
                <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">
                  ${suggestionList}
                </ul>
              </div>`
            : ''
        }
        ${
          hasProducts
            ? `<div class="predictive-search__result-group">
                <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing large-up-hide">${this.escapeHtml(strings.products || '')}</h2>
                <ul id="predictive-search-results-products-list" class="predictive-search__results-list grid grid--2-col-tablet-down grid--4-col-desktop" role="group" aria-labelledby="predictive-search-products">
                  ${productCards}
                </ul>
              </div>`
            : ''
        }
        ${hasSuggestions || hasProducts ? '</div>' : ''}
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="button button--secondary" tabindex="-1" role="option" aria-selected="false" ${searchHref ? `formaction="${this.escapeHtml(searchHref)}"` : ''}>
            ${this.escapeHtml(strings.viewAll || '')}
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>
        ${this.buildLiveRegionText(searchTerm, suggestionItems.length, productItems.length, response)}
      </span>
    `
  }

  getQueryItems (response) {
    const items = this.normaliseItems(
      response?.sections?.queries?.items ||
        response?.sections?.suggestions?.items ||
        response?.sections?.text_suggestions?.items,
    )

    return items.slice(0, this.queryCount)
  }

  getCollectionItems (response) {
    const items = this.normaliseItems(response?.sections?.collections?.items)
    return items.slice(0, this.collectionCount)
  }

  getProductItems (response) {
    return this.normaliseItems(response?.sections?.products?.items).slice(0, this.productCount)
  }

  getSuggestionItems (response) {
    return [...this.getQueryItems(response), ...this.getCollectionItems(response)]
  }

  normaliseItems (items) {
    return Array.isArray(items) ? items.filter(Boolean) : []
  }

  buildTextResultItem (item, idSuffix) {
    const itemObj = typeof item === 'string' ? { query: item } : item
    const href = this.getItemHref(itemObj)
    const title = this.capitalize(
      itemObj.title || itemObj.query || itemObj.label || itemObj.text || '',
    )
    if (!href || !title) return ''

    const isCollection =
      idSuffix.startsWith('collection') ||
      (Boolean(itemObj.link || itemObj.handle) && !itemObj.query && !itemObj.text)
    const itemClass = isCollection
      ? 'predictive-search__list-item predictive-search__list-item--collection'
      : 'predictive-search__list-item'
    const isQueryResult = Boolean(itemObj.query || itemObj.text)

    return `
      <li id="predictive-search-option-${idSuffix}" class="${itemClass}" role="option" aria-selected="false">
        <a href="${this.escapeHtml(href)}" class="predictive-search__item link link--text" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading${isQueryResult ? ' predictive-search__item-query-result' : ''} body-1"${isQueryResult ? ` aria-label="${this.escapeHtml(title)}"` : ''}>${this.escapeHtml(title)}</p>
          </div>
        </a>
      </li>
    `
  }

  async createProductCards (products) {
    const { signal } = this.abortController

    const cards = await Promise.all(
      products.map(async (product, index) => {
        if (!product?.handle || signal.aborted) return ''

        const cached = this.cachedProductCards[product.handle]
        if (cached) return this.wrapProductCard(cached, index)

        try {
          const productUrl = `${window.Shopify.routes.root}products/${product.handle}`
          const separator = productUrl.includes('?') ? '&' : '?'
          const response = await fetch(
            `${productUrl}${separator}section_id=tagalys-product-card`,
            { signal },
          )
          const sectionHtml = await response.text()
          const sectionDocument = window.BAO.sectionRenderer.convertToDOM(sectionHtml)
          const productCardElement = sectionDocument.querySelector('[data-product-card]')

          if (!productCardElement) return ''

          this.cachedProductCards[product.handle] = productCardElement.outerHTML
          return this.wrapProductCard(this.cachedProductCards[product.handle], index)
        } catch (error) {
          if (error?.name === 'AbortError') return ''
          console.error('[TagalysQuickSearch] Failed to render product card', error)
          return ''
        }
      }),
    )

    return cards.join('')
  }

  wrapProductCard (innerHtml, index) {
    return `
      <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item grid__item" role="option" aria-selected="false">
        ${innerHtml}
      </li>
    `
  }

  getItemHref (item) {
    if (typeof item === 'string') {
      return `${this.searchPath}?q=${encodeURIComponent(item)}`
    }

    if (item?.link) return this.localiseHref(item.link)
    if (item?.url) return this.localiseHref(item.url)
    if (item?.onlineStoreUrl) return this.localiseHref(item.onlineStoreUrl)

    if (item?.queryString) {
      return `${this.searchPath}?${item.queryString.replace(/^\?/, '')}`
    }

    if (item?.query) {
      return `${this.searchPath}?q=${encodeURIComponent(item.query)}`
    }

    if (item?.text) {
      return `${this.searchPath}?q=${encodeURIComponent(item.text)}`
    }

    if (item?.handle) {
      return this.localiseHref(`/collections/${item.handle}`)
    }

    return ''
  }

  buildSearchHref (searchTerm, response) {
    const redirectUrl = response?.metadata?.redirect_to_url
    if (redirectUrl) return this.localiseHref(redirectUrl)

    const helpers = this.searchSuggestions?.helpers
    if (typeof helpers?.getEncodedQueryString === 'function') {
      const queryString = helpers.getEncodedQueryString({ query: searchTerm })
      if (queryString) return `${this.searchPath}?${queryString.replace(/^\?/, '')}`
    }

    return `${this.searchPath}?q=${encodeURIComponent(searchTerm)}`
  }

  buildLiveRegionText (searchTerm, suggestionCount, productCount, response) {
    const strings = window.strings?.searchTemplate || {}
    const total =
      response?.sections?.products?.total_count ??
      suggestionCount + productCount

    if (!total) {
      return (strings.noResults || '').replace('[terms]', searchTerm)
    }

    const resultsWithCount = strings.resultsWithCount?.other || ''
    return resultsWithCount.replace('[count]', String(total))
  }

  setLiveRegionLoadingState () {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text')

    this.setLiveRegionText(this.loadingText)
    this.predictiveSearchResults?.setAttribute('loading', true)
    this.setAttribute('loading', true)
  }

  setLiveRegionText (statusText) {
    if (!this.statusElement) return

    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    clearTimeout(this.liveRegionTimer)
    this.liveRegionTimer = setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults (resultsMarkup) {
    if (!this.predictiveSearchResults) return

    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)
    this.predictiveSearchContainer?.classList.remove('empty')

    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults () {
    this.predictiveSearchResults?.removeAttribute('loading')
    this.removeAttribute('loading')
    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]',
    )
    if (countElement) {
      this.setLiveRegionText(countElement.textContent)
    }
  }

  open () {
    document.querySelectorAll('.mega-menu').forEach((menu) => {
      menu.removeAttribute('open')
    })

    this.setAttribute('open', true)
    this.input?.setAttribute('aria-expanded', true)
    this.isOpen = true
  }

  close (clearSearchTerm = false) {
    this.closeResults(clearSearchTerm)
    this.isOpen = false
  }

  closeResults (clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = ''
      this.removeAttribute('results')
    }

    const selected = this.querySelector('[aria-selected="true"]')
    if (selected) selected.setAttribute('aria-selected', false)

    this.input?.setAttribute('aria-activedescendant', '')
    this.removeAttribute('loading')
    this.removeAttribute('open')
    this.input?.setAttribute('aria-expanded', false)
    this.predictiveSearchContainer?.classList.add('empty')
    this.predictiveSearchContainer?.removeAttribute('style')
    this.predictiveSearchResults?.removeAttribute('loading')
  }

  localiseHref (href) {
    if (!href) return ''

    if (href.startsWith('http')) {
      return href.replace(
        window.location.origin,
        `${window.location.origin}${this.localePrefix}`,
      )
    }

    if (href.startsWith(this.localePrefix) || this.localePrefix === '') {
      return href
    }

    return `${this.localePrefix}${href}`
  }

  capitalize (value) {
    const text = String(value || '')
    if (!text) return ''
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  escapeHtml (value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')
  }

  get localePrefix () {
    const root = window.Shopify?.routes?.root ?? '/'
    return root === '/' ? '' : root.replace(/\/$/, '')
  }

  get searchPath () {
    return (
      window.routes?.searchUrl ||
      window.routes?.search_url ||
      `${this.localePrefix}/search`
    )
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', TagalysQuickSearch)
}
